import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { numStr, parseNum } from "@/lib/format";
import { Button, Field, Input, Segmented } from "@/components/kit";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const FIELDS = [
  ["tax_pct", "Impostos sobre vendas (% do faturamento)", "Ex.: alíquota do Simples Nacional"],
  ["card_fee_pct", "Taxa média de cartão (%)", "Média das maquininhas sobre o faturamento"],
  ["delivery_share_pct", "Vendas por apps de entrega (% do faturamento)", "Quanto do faturamento vem de iFood e similares"],
  ["delivery_fee_pct", "Comissão média dos apps (%)", "Cobrada sobre as vendas feitas pelos apps"],
  ["other_pct", "Outras deduções (%)", "Ex.: devoluções, cancelamentos"],
];
const GOALS = [{ value: "valor", label: "Valor em R$" }, { value: "percentual", label: "% do faturamento" }];

export function DreSettingsDialog({ open, onOpenChange, settings }) {
  const qc = useQueryClient();
  const [f, setF] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, numStr(settings?.[k] ?? 0) || "0"])));
  const [goalType, setGoalType] = useState(settings?.goal_type || "valor");
  const [goal, setGoal] = useState(settings?.goal_value ? numStr(settings.goal_value) : "");
  const save = useMutation({
    mutationFn: () => api("/dre/settings", { method: "PUT", body: { ...Object.fromEntries(FIELDS.map(([k]) => [k, parseNum(f[k])])), goal_type: goalType, goal_value: parseNum(goal) } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dre"] }); toast.success("Impostos e meta salvos"); onOpenChange(false); },
    onError: (e) => toast.error(e.message),
  });
  const submit = () => {
    if (FIELDS.some(([k]) => parseNum(f[k]) < 0 || parseNum(f[k]) > 100)) return toast.error("Os percentuais devem ficar entre 0 e 100");
    if (parseNum(goal) < 0 || (goalType === "percentual" && parseNum(goal) > 100)) return toast.error("A meta em percentual deve ficar entre 0 e 100");
    save.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl" data-testid="dre-settings-dialog">
        <DialogTitle>Impostos, taxas e meta</DialogTitle>
        <DialogDescription>Os percentuais são calculados sobre o faturamento de cada mês.</DialogDescription>
        <div className="flex flex-col gap-3">
          {FIELDS.map(([k, label, hint]) => (
            <Field key={k} label={label} hint={hint}><Input inputMode="decimal" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`dre-setting-${k}`} /></Field>
          ))}
          <div className="border-t pt-3 flex flex-col gap-3" style={{ borderColor: "var(--border)" }}>
            <div className="label flex items-center gap-1.5"><Target size={15} /> Meta de lucro líquido mensal</div>
            <Segmented options={GOALS} value={goalType} onChange={setGoalType} testid="dre-goal-type" />
            <Field label={goalType === "valor" ? "Lucro desejado por mês (R$)" : "Lucro desejado (% do faturamento)"} hint="Deixe em branco para não usar meta">
              <Input inputMode="decimal" placeholder={goalType === "valor" ? "Ex.: 15.000,00" : "Ex.: 15"} value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="dre-goal-value" />
            </Field>
          </div>
        </div>
        <Button loading={save.isPending} onClick={submit} data-testid="dre-settings-save">Salvar</Button>
      </DialogContent>
    </Dialog>
  );
}
